import React, { useEffect, useState } from "react";
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { getAuth } from "firebase/auth";
import { useNavigate } from "react-router-dom"; // Import useNavigate for navigation
import "./AdminAccess.css";

// Scroll wrapper for the Add / Edit popups. Inline styles so no other
// stylesheet's .modal rules can override the scrolling.
const modalScrollStyle = {
  maxHeight: "80vh",
  overflowY: "auto",
  overflowX: "hidden",
  paddingRight: "6px",
};

const AdminAccess = () => {
  const navigate = useNavigate(); // Initialize useNavigate
  const [codes, setCodes] = useState([]);
  const [properties, setProperties] = useState([]); // List of properties
  const [newCode, setNewCode] = useState({
    company: "",
    website: "",
    username: "",
    password: "",
    category: "",
    note: "", // Re-add Notes field
  });
  const [selectedCode, setSelectedCode] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userRole, setUserRole] = useState(""); // Assuming you have a way to get the user's role
  const [searchTerm, setSearchTerm] = useState(''); // Search term
  const [sortOption, setSortOption] = useState(''); // Sort option
  const [filteredCodes, setFilteredCodes] = useState([]); // Filtered codes for search and sort
  const [pin, setPin] = useState("");
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [pinError, setPinError] = useState("");

  useEffect(() => {
    const fetchUserData = async () => {
      const auth = getAuth();
      const currentUser = auth.currentUser;

      if (currentUser) {
        const usersRef = collection(db, "users");
        const querySnapshot = await getDocs(usersRef);
        const userDoc = querySnapshot.docs.find((doc) => doc.id === currentUser.uid);

        if (userDoc) {
          setUserRole(userDoc.data().role);
        }
      }
    };

    const fetchCodes = async () => {
      try {
        const codesRef = collection(db, "adminAccess");
        const querySnapshot = await getDocs(codesRef);
        const codesList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setCodes(codesList);
      } catch (error) {
        console.error("Error fetching codes:", error);
      }
    };

    const fetchProperties = async () => {
      try {
        const propertiesRef = collection(db, "properties");
        const querySnapshot = await getDocs(propertiesRef);
        const propertiesList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setProperties(propertiesList);
      } catch (error) {
        console.error("Error fetching properties:", error);
      }
    };

    fetchUserData();
    fetchCodes();
    fetchProperties();
  }, []);

  useEffect(() => {
    filterAndSortCodes();
  }, [codes, searchTerm, sortOption]);

  useEffect(() => {
    // Ensure selectedCode is updated after saving edits
    if (!isEditing && selectedCode) {
      const updatedCode = codes.find(code => code.id === selectedCode.id);
      if (updatedCode) {
        setSelectedCode(updatedCode);
      }
    }
  }, [codes]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewCode((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const docRef = await addDoc(collection(db, "adminAccess"), newCode);
      // Keep the new Firestore id so Details/Edit/Delete work without a refresh
      setCodes([...codes, { id: docRef.id, ...newCode }]);
      setShowForm(false);
      setNewCode({
        company: "",
        website: "",
        username: "",
        password: "",
        category: "",
        note: "", // Reset Notes field
      });
    } catch (error) {
      console.error("Error adding code:", error);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setNewCode(selectedCode);
  };

  const handleSave = async () => {
    try {
      const codeRef = doc(db, "adminAccess", selectedCode.id);
      await updateDoc(codeRef, newCode);
      const updatedCodes = codes.map(code =>
        code.id === selectedCode.id ? { ...code, ...newCode } : code
      );
      setCodes(updatedCodes);
      setSelectedCode({ ...selectedCode, ...newCode });
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating code:", error);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleDelete = async (id) => {
    if (userRole !== "Admin") {
      alert("You do not have permission to delete this code.");
      return;
    }
    try {
      await deleteDoc(doc(db, "adminAccess", id));
      setCodes(codes.filter(code => code.id !== id));
      setSelectedCode(null);
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error("Error deleting code:", error);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSortChange = (e) => {
    setSortOption(e.target.value);
  };

  const filterAndSortCodes = () => {
    let filtered = codes.filter(code =>
      (code.company || "").toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (sortOption === 'companyAsc') {
      filtered.sort((a, b) => (a.company || "").localeCompare(b.company || ""));
    } else if (sortOption === 'companyDesc') {
      filtered.sort((a, b) => (b.company || "").localeCompare(a.company || ""));
    }

    setFilteredCodes(filtered);
  };

  const handleUnlock = () => {
    const correctPin = "0417"; // Replace with your PIN
    if (pin === correctPin) {
      setIsAuthorized(true);
      setPinError("");
    } else {
      setPinError("Incorrect PIN. Please try again.");
    }
  };

  const handlePinCancel = () => {
    setPin("");
    setPinError("");
    navigate(-1); // Go back to the previous page
  };

  if (!isAuthorized) {
    return (
      <div className="pin-overlay">
        <div className="pin-modal">
          <h2>Enter PIN Code</h2>
          <input
            type="password"
            value={pin}
            maxLength={8}
            placeholder="4–8 digit PIN"
            autoFocus
            onChange={(e) => setPin(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleUnlock();
              if (e.key === "Escape") handlePinCancel();
            }}
          />
          {pinError && <p style={{ color: "red" }}>{pinError}</p>}
          <div className="modal-buttons">
            <button className="blue-button" onClick={handleUnlock}>
              Unlock
            </button>
            <button className="green-button" onClick={handlePinCancel}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="codes-container">
      <button className="blue-button" onClick={() => navigate('../PaymentsReceived')}>
        Payments Received
      </button>
      <button className="blue-button" onClick={() => {
        setShowForm(true);
        setNewCode({
          company: "",
          website: "",
          username: "",
          password: "",
          category: "",
          note: "", // Reset Notes field
        });
      }}>Add Admin Code</button>
      {showForm && (
        <>
          <div className="modal-overlay" onClick={() => setShowForm(false)}></div> {/* Add overlay click handler */}
          <div className="modal">
            <div style={modalScrollStyle}>
              <h3>Add Admin Code</h3>
              <form className="form-container" onSubmit={handleSubmit}>
                <label>Company:</label>
                <input type="text" name="company" value={newCode.company} onChange={handleInputChange} required />
                <label>Website:</label>
                <input type="text" name="website" value={newCode.website} onChange={handleInputChange} />
                <label>Username:</label>
                <input type="text" name="username" value={newCode.username} onChange={handleInputChange} />
                <label>Password:</label>
                <input type="text" name="password" value={newCode.password} onChange={handleInputChange} />
                <label>Category:</label>
                <select name="category" value={newCode.category} onChange={handleInputChange} required>
                  <option value="">Select Category</option>
                  <option value="Banking & Financial Services">Banking & Financial Services</option>
                  <option value="Utilities">Utilities</option>
                  <option value="Internet & Cable Providers">Internet & Cable Providers</option>
                  <option value="Phone & Mobile Services">Phone & Mobile Services</option>
                  <option value="Property & HOA Management Portals">Property & HOA Management Portals</option>
                  <option value="Insurance Providers">Insurance Providers</option>
                  <option value="Government & Municipal Portals">Government & Municipal Portals</option>
                  <option value="Email & Communication Tools">Email & Communication Tools</option>
                  <option value="Security & Alarm Systems">Security & Alarm Systems</option>
                  <option value="Cloud Storage & Document Access">Cloud Storage & Document Access</option>
                  <option value="Other">Other</option>
                </select>
                <label>Notes:</label>
                <textarea name="note" value={newCode.note} onChange={handleInputChange} />
                <div className="modal-buttons">
                  <button type="submit" className="blue-button">Save</button>
                  <button type="button" className="green-button" onClick={() => setShowForm(false)}>Close</button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      <div className="search-sort-container">
        <input
          type="text"
          placeholder="Search by Company"
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <select value={sortOption} onChange={handleSortChange}>
          <option value="">Sort By</option>
          <option value="companyAsc">Company (A-Z)</option>
          <option value="companyDesc">Company (Z-A)</option>
        </select>
      </div>

      {/* Scrolls up/down and left/right; header row stays pinned */}
      <div
        className="table-container admin-table-scroll"
        style={{ overflowX: "auto", overflowY: "auto", maxHeight: "65vh" }}
      >
        <table>
          <thead>
            <tr>
              <th>Company</th>
              <th>Website</th>
              <th>Username</th>
              <th>Password</th>
              <th>Category</th>
              <th>Notes</th> {/* Add Notes column */}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCodes.map(code => (
              <tr key={code.id}>
                <td>{code.company}</td>
                <td>{code.website}</td>
                <td>{code.username}</td>
                <td>{code.password}</td>
                <td>{code.category}</td>
                <td>{code.note}</td> {/* Add Notes field */}
                <td>
                  <button className="blue-button" onClick={() => setSelectedCode(code)}>Details</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedCode && (
        <>
          <div className="modal-overlay" onClick={() => setSelectedCode(null)}></div> {/* Add overlay click handler */}
          <div className="modal">
            <div style={modalScrollStyle}>
              {isEditing ? (
                <div className="form-container">
                  <h3>Edit Code</h3>
                  <label>Company:</label>
                  <input type="text" name="company" value={newCode.company} onChange={handleInputChange} required />
                  <label>Website:</label>
                  <input type="text" name="website" value={newCode.website} onChange={handleInputChange} />
                  <label>Username:</label>
                  <input type="text" name="username" value={newCode.username} onChange={handleInputChange} />
                  <label>Password:</label>
                  <input type="text" name="password" value={newCode.password} onChange={handleInputChange} />
                  <label>Category:</label>
                  <select name="category" value={newCode.category} onChange={handleInputChange} required>
                    <option value="">Select Category</option>
                    <option value="Banking & Financial Services">Banking & Financial Services</option>
                    <option value="Utilities">Utilities</option>
                    <option value="Internet & Cable Providers">Internet & Cable Providers</option>
                    <option value="Phone & Mobile Services">Phone & Mobile Services</option>
                    <option value="Property & HOA Management Portals">Property & HOA Management Portals</option>
                    <option value="Insurance Providers">Insurance Providers</option>
                    <option value="Government & Municipal Portals">Government & Municipal Portals</option>
                    <option value="Email & Communication Tools">Email & Communication Tools</option>
                    <option value="Security & Alarm Systems">Security & Alarm Systems</option>
                    <option value="Cloud Storage & Document Access">Cloud Storage & Document Access</option>
                    <option value="Other">Other</option>
                  </select>
                  <label>Notes:</label>
                  <textarea name="note" value={newCode.note} onChange={handleInputChange} />
                  <div className="modal-buttons">
                    <button onClick={handleSave} className="blue-button">Save</button>
                    <button onClick={handleCancel} className="green-button">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="popup-content">
                  <h3>Code Details</h3>
                  <p><strong>Company:</strong> {selectedCode.company}</p>
                  <p><strong>Website:</strong> {selectedCode.website}</p>
                  <p><strong>Username:</strong> {selectedCode.username}</p>
                  <p><strong>Password:</strong> {selectedCode.password}</p>
                  <p><strong>Category:</strong> {selectedCode.category}</p>
                  <p style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    <strong>Notes:</strong> {selectedCode.note}
                  </p> {/* Add Notes field */}
                  <div className="modal-buttons">
                    <button onClick={handleEdit} className="blue-button">Edit</button>
                    {userRole === "Admin" && (
                      <button onClick={() => setShowDeleteConfirm(true)} className="red-button">Delete</button>
                    )}
                    <button onClick={() => setSelectedCode(null)} className="green-button">Close</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Are you sure you want to delete?</h3>
            <div className="modal-buttons">
              <button onClick={() => handleDelete(selectedCode.id)} className="red-button">Yes</button>
              <button onClick={() => setShowDeleteConfirm(false)} className="green-button">No</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAccess;
